package modysy.simulation.collision

import modysy.datastructures.Vector2
import modysy.model.GameObject

import scala.collection.mutable
import scala.collection.mutable.ListBuffer

/** Provides fast collision checking through spatial hash mapping.
  *
  * The game field is divided into squared cells. Based on the bounding box of an object, it is placed in all cells
  * that it resides in. This spatial information enables to omit checking collisions between objects that are not
  * nearby.
  *
  * Accuracy and fail-safety depends on the cellSize in relation to the size of the tested objects.
  * Generally an higher cellSize leads to less errors but lower performance.
  *
  * @param width    the width of the game field
  * @param height   the height of the game field
  * @param cellSize the length of a squared cell
  */
class SpatialHashManager(width: Int, height: Int, cellSize: Int)
    extends CollisionEngine {
  private val colAmount = Math.ceil(width.toDouble / cellSize).toInt
  private val rowAmount = Math.ceil(height.toDouble / cellSize).toInt

  private val buckets = {
    val builder = Vector.newBuilder[ListBuffer[GameObject]]

    for (i <- 1 to colAmount * rowAmount)
      builder += new ListBuffer[GameObject]

    builder.result()
  }

  private val alreadyChecked = new mutable.HashSet[(GameObject, GameObject)]
  private var registeredObjects = Seq[GameObject]()

  private def column(point: Vector2) = (point.x / cellSize).toInt

  private def row(point: Vector2) = (point.y / cellSize).toInt

  private def calculateBucketID(point: Vector2) =
    column(point) + row(point) * colAmount

  override def register(objects: Seq[GameObject]): Unit = {
    buckets foreach {
      _.clear
    }
    objects foreach registerObject
    registeredObjects = objects
  }

  override def calculateCollisions(): Seq[CollisionPair] = {
    alreadyChecked.clear()

    val collisions = Seq.newBuilder[CollisionPair]

    for (o <- registeredObjects;
         otherO <- getNearby(o)
         if otherO != o && !alreadyChecked.contains((o, otherO)) && !alreadyChecked
           .contains((otherO, o))) {
      alreadyChecked.add((otherO, o))
      collisions ++= findCollision(o, otherO)
    }

    collisions.result()
  }

  override def calculateCollisions(obj: GameObject): Seq[CollisionPair] = {
    getNearby(obj) flatMap {
      findCollision(obj, _)
    }
  }

  override def findOnPoint(point: Vector2): Seq[GameObject] = {
    val bucketID = calculateBucketID(point)
    val contained = buckets(bucketID)

    contained.filter(_.body.contains(point))
  }

  /** Registers the given object on this SpatialHashManager so
    * proximity with this object can be taken into account
    * when calling getNearby(...) later.
    *
    * An object may not change its position once registered.
    *
    * @param obj the object to be registered
    */
  private def registerObject(obj: GameObject): Unit = {
    val bucketIDs = getBucketIDsForObject(obj)

    bucketIDs.foreach(buckets(_) += obj)
  }

  /** Returns a list of IDs of the buckets containing the given obj.
    *
    * @param obj The object whose bucketIDs are returned
    * @return A List of IDs for buckets containing the obj
    */
  private def getBucketIDsForObject(obj: GameObject): Seq[Int] = {
    val bBox = boundingBox(obj)

    val columns = (bBox.bottomLeft.x / cellSize).toInt to (bBox.topRight.x / cellSize).toInt
    val rows = (bBox.topRight.y / cellSize).toInt to (bBox.bottomLeft.y / cellSize).toInt

    for (column <- columns;
         row <- rows
         if (0 <= column && column < colAmount && 0 <= row && row < rowAmount))
      yield column + row * colAmount
  }

  /** Returns a list of GameObjects that are close enough to the
    * given GameObject so that they may collide.
    *
    * @param obj the GameObject whose near other GameObjects are returned
    * @return A list of near GameObjects
    */
  private def getNearby(obj: GameObject): Seq[GameObject] =
    getBucketIDsForObject(obj) flatMap buckets
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/simulation/collision/SpatialHashManager.scala